import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useStatus } from '../contexts/StatusContext';
import { classifyStatus } from '../utils/checker';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiAlertTriangle, FiXCircle, FiClock, FiGlobe, FiArrowRight, FiInfo } = FiIcons;

const StatusCard = () => {
  const { currentResult, isChecking, settings } = useStatus();

  // Helper function to get domain from URL
  const getDomainFromUrl = (url) => {
    try {
      return new URL(url).hostname;
    } catch {
      return null;
    }
  };

  if (isChecking) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl card-shadow p-4 sm:p-6"
      >
        <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <div className="text-gray-600 dark:text-gray-400 text-center sm:text-left">
            <p className="font-medium text-sm sm:text-base">Checking website status<span className="loading-dots"></span></p>
            <p className="text-xs sm:text-sm">This may take a few seconds</p>
          </div>
        </div>
      </motion.div>
    );
  }

  if (!currentResult) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-6 sm:p-8"
      >
        <div className="text-center text-gray-500 dark:text-gray-400">
          <SafeIcon icon={FiGlobe} className="h-10 sm:h-12 w-10 sm:w-12 mx-auto mb-4 opacity-50" />
          <p className="text-base sm:text-lg font-medium mb-2">Ready to check website status</p>
          <p className="text-xs sm:text-sm">Enter a URL or domain above to get started</p>
        </div>
      </motion.div>
    );
  }

  const status = classifyStatus(currentResult, settings.thresholdMs);
  const isLimited = currentResult.mode === 'Limited';
  const domain = getDomainFromUrl(currentResult.finalUrl || currentResult.normalizedUrl);

  const statusConfig = {
    'Up': {
      icon: FiCheckCircle,
      color: 'text-green-600 dark:text-green-400',
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800'
    },
    'Degraded': {
      icon: FiAlertTriangle,
      color: 'text-yellow-600 dark:text-yellow-400',
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800'
    },
    'Down': {
      icon: FiXCircle,
      color: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800'
    },
    'Partial': {
      icon: FiAlertTriangle,
      color: 'text-yellow-600 dark:text-yellow-400',
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800'
    }
  };

  const config = statusConfig[status];
  const displayStatus = isLimited && status !== 'Down' ? `${status} (Limited)` : status;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl card-shadow p-4 sm:p-6 border ${config.bg} ${config.border}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            {domain && (
              <img 
                src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
                alt={`${domain} favicon`}
                className="w-6 sm:w-8 h-6 sm:h-8 rounded"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <SafeIcon icon={config.icon} className={`h-6 sm:h-8 w-6 sm:w-8 ${config.color}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className={`text-lg sm:text-xl font-bold ${config.color} break-words`}>
              {currentResult.input} is {displayStatus}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">
              Checked on {new Date(currentResult.timestamp).toLocaleString()}
            </p>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${config.color} ${config.bg} whitespace-nowrap self-start`}>
          {displayStatus}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white dark:bg-gray-700 rounded-lg p-3 sm:p-4">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiClock} className="h-3 sm:h-4 w-3 sm:w-4 text-gray-500" />
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Response Time</span>
          </div>
          <p className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white">
            {currentResult.responseTimeMs}ms
          </p>
        </div>

        <div className="bg-white dark:bg-gray-700 rounded-lg p-3 sm:p-4">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiGlobe} className="h-3 sm:h-4 w-3 sm:w-4 text-gray-500" />
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">HTTP Status</span>
          </div>
          <p className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white">
            {currentResult.httpCode || (isLimited ? 'Limited' : 'N/A')}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-700 rounded-lg p-3 sm:p-4">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiArrowRight} className="h-3 sm:h-4 w-3 sm:w-4 text-gray-500" />
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Redirects</span>
          </div>
          <p className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white">
            {currentResult.redirects?.length || 0}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-700 rounded-lg p-3 sm:p-4">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiCheckCircle} className="h-3 sm:h-4 w-3 sm:w-4 text-gray-500" />
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">SSL/TLS</span>
          </div>
          <p className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white">
            {currentResult.finalUrl?.startsWith('https://') ? 'Yes' : 'No'}
          </p>
        </div>
      </div>

      {currentResult.reason && (
        <div className="mt-4 p-3 sm:p-4 bg-white dark:bg-gray-700 rounded-lg">
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 break-words">
            <span className="font-medium">Details:</span> {currentResult.reason}
          </p>
        </div>
      )}

      {currentResult.corsBlocked && (
        <div className="mt-4 p-3 sm:p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <div className="flex items-start space-x-2">
            <SafeIcon icon={FiInfo} className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-orange-800 dark:text-orange-400">
                <span className="font-medium">Limited Check:</span> Using fallback method due to CORS restrictions.
              </p>
              {isLimited && (
                <div className="mt-2 text-xs text-orange-700 dark:text-orange-300">
                  Limited check results:
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>DNS Resolution: {currentResult.dnsResolved ? 'Success' : 'Failed'}</li>
                    <li>Favicon Test: {currentResult.faviconReachable ? 'Success' : 'Failed'}</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default StatusCard;